const HERO_IMAGE =
  'https://images.unsplash.com/photo-1529900748604-07564a03e7a6?auto=format&fit=crop&w=1600&q=80';

/** Featured DIY guide: convert part of a basement into a small turf soccer practice area for kids 3–13 */
export const BASEMENT_TURF_SOCCER_POST = {
  id: 'featured-basement-turf-soccer-kids',
  slug: 'basement-turf-soccer-practice-area-kids-guide',
  title: 'How to Turn Part of Your Basement Into a Small Turf Soccer Practice Area',
  excerpt:
    'Step-by-step DIY guide to carve out a safe indoor turf zone for family soccer practice—sized for a 3-year-old and a 13-year-old, with materials, layout tips, drills, and a realistic budget.',
  publishedAt: '2026-07-25T18:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Home Training Desk',
  blogCategory: 'club-news',
  audience: 'Ages 3–13',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Project overview',
      items: [
        { value: '8 steps', label: 'Basement to turf zone' },
        { value: '10×12 ft+', label: 'Minimum useful size' },
        { value: 'Ages 3–13', label: 'Dual-age layout' },
        { value: '$400–1,200', label: 'Typical DIY budget' },
      ],
    },
    {
      type: 'panel',
      title: 'What you are building',
      paragraphs: [
        'You do not need to turf the entire basement. Most families convert one clear rectangle—often 10×12 ft to 12×20 ft—into a padded, low-bounce practice lane with goals at one end and open floor at the other for dribbling and passing.',
        'A 3-year-old needs a soft surface, low goals, and a ball that will not ricochet off concrete. A 13-year-old needs enough run-up for shooting, first-touch reps, and 1v1 space. This guide walks through planning, materials, installation, and age-split drills so both kids can use the same zone safely.',
        'Before you buy anything: measure ceiling height (aim for 8 ft clear minimum above the play zone), check for moisture on walls and floor, and photograph your layout. If you have a photo of your basement, mark the proposed turf rectangle on it and confirm nothing hangs into that air space—ductwork, pipes, and low beams are the usual limits.',
      ],
      chips: ['DIY friendly', 'Partial basement', 'Indoor soccer', 'Family practice'],
    },
    {
      type: 'panel',
      title: 'Step 1 — Measure and choose your zone',
      orderedList: [
        'Clear one wall-adjacent rectangle. Push storage to the opposite side so the turf area stays open year-round.',
        'Measure length × width in feet. Write down ceiling height at the center of the zone and at the lowest beam.',
        'Mark a “no-kick” buffer of 18–24 inches from walls, furnaces, water heaters, and electrical panels.',
        'Sketch two zones inside the rectangle: a 6–8 ft “little kickers” end (softer, smaller goals) and a longer strip for your 13-year-old’s passing and shooting.',
        'Take photos from each corner. You will use these when ordering turf rolls and cutting puzzle mats to fit around posts or stairs.',
      ],
      callout:
        'Rule of thumb: 10×12 ft works for ball control and short passing. 12×16 ft or larger lets a teenager take 5–8 step run-ups and practice light crossing.',
    },
    {
      type: 'table',
      title: 'Step 2 — Safety checklist before you install',
      intro:
        'Basements fail projects when moisture, low ceilings, or hard surfaces are ignored. Complete this list before unrolling turf.',
      headers: ['Check', 'Why it matters', 'What to do'],
      rows: [
        [
          'Ceiling height',
          'Headers and lobs need headroom; low beams hurt.',
          'Need 8 ft clear minimum in the play zone. Hang a tennis ball on a string from the lowest point—if it swings into the kicking lane, shift the zone or pad the beam.',
        ],
        [
          'Floor moisture',
          'Trapped moisture causes mold under turf.',
          'Tape a 2×2 ft plastic sheet to bare concrete for 48 hours. Condensation underneath means seal the slab or use a vapor-barrier underlayment first.',
        ],
        [
          'Wall protection',
          'Missed shots hit drywall and pipes.',
          'Install 2–4 ft tall impact panels (rubber gym tiles, old carpet squares, or commercial wall padding) on the goal-end wall.',
        ],
        [
          'HVAC & utilities',
          'Furnaces need clearance; balls should not hit gas lines.',
          'Keep turf and goals outside manufacturer clearance zones. Use netting as a backstop, not as a substitute for code clearance.',
        ],
        [
          'Egress & storage',
          'Basement bedrooms and exits must stay unobstructed.',
          'Do not block windows, stairs, or sump pumps. Store balls in wall bins, not on the turf.',
        ],
        [
          'Lighting',
          'Dim basements cause tripping and bad technique.',
          'Add 4,000–6,000 K LED shop lights or panel lights over the zone—bright, even light helps both age groups.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Step 3 — Materials shopping list',
      intro:
        'Order slightly more than your measured area (5–10% waste). For a 12×14 ft zone, the quantities below are a solid starting point.',
      headers: ['Item', 'Purpose', 'Notes for kids 3 & 13'],
      rows: [
        [
          'Artificial turf roll (½–¾ in pile)',
          'Playing surface',
          'Choose indoor/sports turf with drainage holes. Shorter pile (½ in) is easier for toddlers to run on; ¾ in feels more like outdoor grass for teens.',
        ],
        [
          'Foam underlayment or rubber sports tiles',
          'Cushion + joint protection',
          '½–¾ in EVA foam or ⅜ in rubber tiles over concrete. Critical for the 3-year-old; teens still benefit on hard slabs.',
        ],
        [
          'Vapor barrier (6 mil poly)',
          'Moisture block',
          'Full coverage on concrete if your tape test showed any dampness.',
        ],
        [
          'Carpet tape / turf adhesive / hook strips',
          'Hold layers in place',
          'Removable double-sided carpet tape works for renters; glue only if you own and accept permanent install.',
        ],
        [
          'Wall padding or rubber tiles',
          'Backstop protection',
          'Cover the full width behind mini goals.',
        ],
        [
          'Pop-up or PVC mini goals (2 sizes)',
          'Age-appropriate targets',
          '3×2 ft for the little one; 5×3 ft or 6×4 ft for the teen. Weighted bases for indoor use.',
        ],
        [
          'Ball stop net (10–12 ft wide)',
          'Catch hard shots',
          'Hang from ceiling joists or a freestanding frame behind the goal.',
        ],
        [
          'Cone set + flat markers',
          'Drills & boundaries',
          'Bright colors; soft cones for the toddler lane.',
        ],
        [
          'Size 1–2 ball (foam or felt)',
          '3-year-old',
          'Low bounce, quiet indoors, safe for furniture.',
        ],
        [
          'Size 4–5 ball',
          '13-year-old',
          'Size 4 if they are small for age; size 5 for full teen practice. Slightly deflated = quieter and less rebound.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Step 4 — Prep the floor (1 weekend afternoon)',
      orderedList: [
        'Move everything out of the marked rectangle. Sweep and vacuum thoroughly.',
        'Degrease and dry the concrete. Fill small cracks with concrete patch; let cure per label.',
        'If using a vapor barrier, roll out 6 mil poly with 6-inch overlaps taped shut. Run edges 2 inches up the wall and tape.',
        'Lay foam underlayment or interlocking rubber tiles wall to wall inside the zone. Stagger seams like hardwood; no gaps at edges.',
        'Trim around posts or stair footings with a utility knife. Leave a ¼-inch expansion gap at walls.',
      ],
    },
    {
      type: 'panel',
      title: 'Step 5 — Install the turf',
      orderedList: [
        'Unroll turf and let it relax flat for 2–4 hours (or overnight) so curl disappears.',
        'Position so pile direction matches your main dribbling line—usually toward the goal.',
        'Cut to fit with a sharp utility knife and extra blades. Number pieces if you need multiple strips.',
        'Seam strips with outdoor carpet tape on the underside, or use seaming tape + adhesive made for synthetic turf.',
        'Secure perimeter with hook strips, tape, or a narrow tack strip along walls—avoid lumps where kids plant their feet.',
        'Brush turf fibers upright with a stiff broom. Infill is optional indoors; if you add sand/rubber, use a light indoor-rated amount or skip it for easier cleanup.',
      ],
      callout:
        'Leave a 2–3 ft “landing strip” of rubber tile or bare hard floor beyond the turf if space allows—good for goalie footwork and storing folding goals.',
    },
    {
      type: 'panel',
      title: 'Step 6 — Set up goals, nets, and boundaries',
      list: [
        'Place the smaller goal on the padded wall side for your 3-year-old; swap to the larger goal when your teen practices.',
        'Hang the ball-stop net 3–4 ft behind the goal, angled slightly forward to absorb shots.',
        'Mark a “no shooting” line 6 ft from the goal for the toddler—tape on turf or flat orange discs.',
        'Use cones to define a 6×8 ft mini grid inside the zone for close control; the outer lane is for longer passes.',
        'Store balls in a wall-mounted bin or milk crate—never underfoot on the turf edge.',
        'Add a small whiteboard or chalk strip on the wall for session plans and keeping score during sibling challenges.',
      ],
    },
    {
      type: 'panel',
      title: 'Step 7 — Age-split practice ideas on the same turf',
      paragraphs: [
        'Rotate sessions rather than mixing full-speed teen shots beside a toddler. Ten minutes per kid with a shared warm-up works well.',
      ],
    },
    {
      type: 'table',
      title: 'Drills for your 3-year-old (10–15 minutes)',
      headers: ['Drill', 'Setup', 'Coaching cue'],
      rows: [
        [
          'Red light / green light dribble',
          'Foam ball, 6 ft lane',
          '“Toes tap the ball on green; freeze on red.” Builds listening and light touches.',
        ],
        [
          'Knock down cones',
          '4 soft cones in a line',
          'Walk and push ball into each cone. Celebrate every hit—no power needed.',
        ],
        [
          'Parent pass & tap-in',
          'Kneel 3 ft from goal',
          'Roll ball slowly; child taps into mini goal. One foot only if they want structure.',
        ],
        [
          'Animal walks on turf',
          'No ball',
          'Bear crawl, bunny hop, crab walk—builds balance on the new surface.',
        ],
        [
          'Clean-up race',
          'Scatter 6 balls',
          'Carry balls to bin one at a time—ends practice on a win.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Drills for your 13-year-old (20–30 minutes)',
      headers: ['Drill', 'Setup', 'Coaching cue'],
      rows: [
        [
          'Wall passing (rebounder or padded wall)',
          '6–10 ft from wall',
          'Two-touch rhythm: receive across body, pass back. 50 reps each foot.',
        ],
        [
          'Cone weave + finish',
          '5 cones, 1 yd apart, mini goal',
          'Explode through weave; place finish low corners. 3 sets each foot.',
        ],
        [
          '1v1 to mini goal',
          'Narrow channel 8 ft wide',
          'Sibling or parent as defender. Win the channel; no slide tackles indoors.',
        ],
        [
          'First-touch gates',
          'Two cone gates 4 ft apart',
          'Serve from hands; control through gate with one touch, pass back.',
        ],
        [
          'Weak-foot only circuit',
          'Full zone',
          '10 passes, 5 juggles, 3 shots—all left foot (or non-dominant). Track weekly.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Step 8 — Maintain the space',
      list: [
        'Vacuum turf weekly with a brush attachment; pick up chalk and tape residue.',
        'Wipe spills immediately—juice and sports drinks stain fibers.',
        'Rotate goal position every month so turf wears evenly.',
        'Re-tape lifting seams as soon as you feel an edge; trip hazards matter more than perfect looks.',
        'Air out the basement: run a dehumidifier to 45–55% RH to protect foam layers.',
        'Inspect wall padding after heavy shooting sessions; replace torn sections.',
      ],
    },
    {
      type: 'table',
      title: 'Budget snapshot (12×14 ft zone, mid-range materials)',
      headers: ['Item', 'Approx. cost (USD)', 'Save money tip'],
      rows: [
        ['Turf (170 sq ft + waste)', '$200–450', 'Buy remnant rolls from local installers'],
        ['Foam underlayment / rubber tiles', '$120–280', 'Interlocking gym tiles on sale at warehouse clubs'],
        ['Wall padding + net', '$80–200', 'DIY net from deer fencing + pool noodles on frame'],
        ['Goals (2 sizes) + cones', '$60–150', 'PVC goals are cheaper than folding aluminum'],
        ['Tape, adhesive, misc.', '$40–80', 'Skip infill indoors'],
        ['Total', '$500–1,160', 'Phase 1: underlayment + one goal + toddler ball first'],
      ],
    },
    {
      type: 'panel',
      title: 'Sample weekend build timeline',
      orderedList: [
        'Friday evening: moisture test, final measurements, order materials.',
        'Saturday AM: clear zone, vapor barrier, lay underlayment.',
        'Saturday PM: unroll turf, cut, seam, secure edges.',
        'Sunday AM: wall pads, hang net, set goals, mark lines.',
        'Sunday PM: 10-minute session with each kid—adjust layout before bolting anything permanent.',
      ],
      callout:
        'If your basement photo shows low ductwork or a pole in the center, build an L-shaped turf instead of one rectangle. Kids adapt quickly to corners and short walls.',
    },
    {
      type: 'panel',
      title: 'When to call a pro',
      list: [
        'Persistent water on the floor or foundation cracks that widen seasonally.',
        'Ceiling under 7 ft 6 in in the only usable area—consider garage or outdoor turf instead.',
        'You need electrical work for new lights or outlets near the zone.',
        'You want wall-to-wall custom seaming on a full basement floor over 300 sq ft.',
      ],
    },
  ],
  relatedPostsTitle: 'More home & youth soccer guides',
  relatedPosts: [
    {
      slug: 'soccer-forward-striker-drills-free-youtube-videos',
      title: 'Soccer Forward & Striker Drills + Free YouTube Videos',
      blurb: 'finishing and 1v1 ideas for your teen’s basement sessions.',
    },
    {
      slug: 'soccer-defender-drills-free-youtube-videos',
      title: 'Soccer Defender Drills + Free YouTube Training Videos',
      blurb: '1v1 and positioning reps in tight spaces.',
    },
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware',
      blurb: 'take full-field practice outdoors when weather allows.',
    },
  ],
  referenceLinks: [
    {
      label: 'U.S. Soccer — Player Development Initiatives (age-appropriate ball sizes)',
      url: 'https://www.ussoccer.com/stories/2016/08/us-soccer-player-development-initiatives',
    },
    {
      label: 'EPA — A Brief Guide to Mold, Moisture and Your Home',
      url: 'https://www.epa.gov/mold/brief-guide-mold-moisture-and-your-home',
    },
    {
      label: 'Asphalt & indoor turf maintenance basics (Synthetic Turf Council)',
      url: 'https://syntheticturfcouncil.org/',
    },
  ],
};
